import { TargetPriceChart } from '@design/charts/target-price-chart/TargetPriceChart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { usePriceHistory } from '@terminal/react-query-hooks/prices/usePriceHistory';
import { useMultipleSnakesMeta } from '@terminal/react-query-hooks/snakes/useMultipleSnakesMeta';
import { getEntityPriceVenue } from '@terminal/services/price/price-service';
import { useTranslation } from 'react-i18next';
import styles from './TargetPriceSection.module.scss';
import { TargetItem } from './target-item/TargetItem';
import {
  getOneYearAgoDate,
  getTargetPriceValues,
  TARGET_PRICE_SNAKES,
} from './utils';

interface TargetPriceSectionProps {
  entityTag: string;
}

export const TargetPriceSection = ({ entityTag }: TargetPriceSectionProps) => {
  const { t } = useTranslation(['earnings']);

  const snakeKeys = Object.values(TARGET_PRICE_SNAKES).map(
    snake => `${entityTag}.${snake}`
  );
  const { data = [], isFetching } = useMultipleSnakesMeta(snakeKeys);

  const { data: entityData, isFetched: isEntityFetched } = useEntity(entityTag);
  const venue = entityData?.data
    ? getEntityPriceVenue(entityData.data)
    : undefined;

  const isWaitingForVenue = !isEntityFetched;

  // `isEntityFetched` only means the entity query settled — it's true even when
  // it errored or resolved without data, leaving `venue` undefined. Gate the
  // request on the resolved venue so a settled-but-unresolved entity can't fire
  // a US-defaulted v2 request for what may be a non-US instrument.
  const hasResolvedVenue = !!venue;

  const {
    data: priceData,
    isFetching: isPriceFetching,
    isError: isPriceError,
    error: priceError,
  } = usePriceHistory({
    entityTag,
    exchange: venue?.exchange,
    country: venue?.country,
    enabled: !isWaitingForVenue && hasResolvedVenue,
  });

  if (isFetching || isPriceFetching || isWaitingForVenue) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '100px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isPriceError) {
    throw new Error(priceError?.message);
  }

  const oneYearAgoDate = getOneYearAgoDate();
  const filteredPriceData = (priceData ?? []).filter(
    price => price.time >= oneYearAgoDate
  );

  const mostRecentPrice = filteredPriceData[filteredPriceData.length - 1];

  if (!mostRecentPrice || !data.length) {
    return null;
  }

  const { high, average, low } = getTargetPriceValues({
    data,
    mostRecentPrice: mostRecentPrice.close,
    entityTag,
  });

  const averageTargetChange =
    ((average.value ?? 0) - mostRecentPrice.close) / mostRecentPrice.close;

  const chartTimeseries = filteredPriceData.map(price => ({
    date: price.time,
    price: price.close,
  }));

  return (
    <div className={styles.root}>
      <div className={styles.titleLabel}>
        {t('earnings:earningsInsight.drawer.earningsPreview.targetPrice')}
      </div>
      <div className={styles.flexRow}>
        <TargetPriceChart
          timeseries={chartTimeseries}
          targetPrice={{
            high: high.value ?? 0,
            average: average.value ?? 0,
            low: low.value ?? 0,
          }}
        />
        <div className={styles.targetsContainer}>
          <TargetItem
            titleKey="highest"
            value={high.value}
            isNegative={high.isNegative}
            currency={high.currency}
          />
          <TargetItem
            titleKey="average"
            value={average.value}
            isNegative={average.isNegative}
            currency={average.currency}
            isActive
            changeValue={averageTargetChange}
            showChange
          />
          <TargetItem
            titleKey="lowest"
            value={low.value}
            isNegative={low.isNegative}
            currency={low.currency}
          />
        </div>
      </div>
    </div>
  );
};
